import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { FaBox, FaEye, FaTruck, FaCheckCircle, FaClock, FaRupeeSign } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminOrders.css";
import { setPageMeta } from "../utils/seo";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deliveryStatuses, setDeliveryStatuses] = useState({});
  const [loadingDelivery, setLoadingDelivery] = useState({});

  useEffect(() => {
    setPageMeta({
      title: "Admin Orders | Banat India",
      description:
        "Manage customer orders and shipment status in the Banat India admin panel.",
      keywords: "admin orders, Banat India admin, order management",
    });
  }, []);

  // Function to fetch delivery status from Shiprocket
  const fetchDeliveryStatus = async (shipmentId) => {
    if (!shipmentId) return null;
    
    try {
              const response = await fetch(`https://shiprocket-backend-maeh.onrender.com/track-shipment/${shipmentId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        
        return null;
      }
    } catch (error) {
      
      return null;
    }
  };

  // Function to get delivery status for all orders
  const loadDeliveryStatuses = useCallback(async (ordersList) => {
    const statuses = {};
    const loadingStates = {};
    
    for (const order of ordersList) {
      if (order.shiprocket_order_id || order.shipment_id) {
        const shipmentId = order.shiprocket_order_id || order.shipment_id;
        loadingStates[order.orderId] = true;
        
        const deliveryData = await fetchDeliveryStatus(shipmentId);
        if (deliveryData) {
          statuses[order.orderId] = {
            status: deliveryData.status || 'Unknown',
            awb: deliveryData.awb || 'N/A',
            courier: deliveryData.courier_name || 'N/A',
            etd: deliveryData.etd || null,
            trackUrl: deliveryData.track_url || null
          };
        } else {
          statuses[order.orderId] = {
            status: 'Not Available',
            awb: 'N/A',
            courier: 'N/A',
            etd: null,
            trackUrl: null
          };
        }
        
        loadingStates[order.orderId] = false;
      }
    }
    
    setDeliveryStatuses(statuses);
    setLoadingDelivery(loadingStates);
  }, []);

  // Function to get delivery status icon
  const getDeliveryStatusIcon = (status) => {
    if (!status) return <FaClock />;
    
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered') || lowerStatus.includes('completed')) {
      return <FaCheckCircle style={{ color: '#28a745' }} />;
    } else if (lowerStatus.includes('in transit') || lowerStatus.includes('shipped')) {
      return <FaTruck style={{ color: '#007bff' }} />;
    } else if (lowerStatus.includes('pending') || lowerStatus.includes('processing')) {
      return <FaClock style={{ color: '#ffc107' }} />;
    } else {
      return <FaClock style={{ color: '#6c757d' }} />;
    }
  };

  // Function to get delivery status color
  const getDeliveryStatusColor = (status) => {
    if (!status) return '#6c757d';
    
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered') || lowerStatus.includes('completed')) {
      return '#28a745';
    } else if (lowerStatus.includes('in transit') || lowerStatus.includes('shipped')) {
      return '#007bff';
    } else if (lowerStatus.includes('pending') || lowerStatus.includes('processing')) {
      return '#ffc107';
    } else {
      return '#6c757d';
    }
  };

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const database = getDatabase();
        const ordersRef = ref(database, "orders");
        
        
        
        const snapshot = await get(ordersRef);
        
        if (snapshot.exists()) {
          const ordersData = snapshot.val();
          const allOrders = [];

          // Process each user's orders
          Object.entries(ordersData).forEach(([userId, userOrders]) => {
            if (userOrders && typeof userOrders === 'object') {
              const userOrderList = Object.entries(userOrders).map(([orderKey, order]) => ({
                orderId: orderKey,
                userId: userId,
                createdAt: order.createdAt || order.timestamp || new Date().toISOString(), // Ensure createdAt exists
                ...order
              }));
              
              allOrders.push(...userOrderList);
            }
          });
          
          
          
          setOrders(allOrders);
          
          // Load delivery statuses for all orders
          if (allOrders.length > 0) {
            loadDeliveryStatuses(allOrders);
          }
        } else {
          
          createSampleOrders();
        }
        
        setLoading(false);
      } catch (error) {
        
        createSampleOrders();
        setLoading(false);
      }
    };

    const createSampleOrders = () => {
      const sampleOrders = [
        {
          orderId: "order-001",
          userId: "user-001",
          total: "750.00",
          status: "paid",
          paymentId: "pay_test123",
          createdAt: new Date().toISOString(),
          shiprocket_order_id: "SR123456",
          shiprocket_awb: "AWB123456"
        },
        {
          orderId: "order-002", 
          userId: "user-001",
          total: "500.00",
          status: "pending",
          paymentId: "pay_test456",
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          shiprocket_order_id: "SR123457",
          shiprocket_awb: "AWB123457"
        },
        {
          orderId: "order-003",
          userId: "user-002",
          total: "1200.00",
          status: "paid",
          paymentId: "pay_test789",
          createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
          shiprocket_order_id: "SR123458",
          shiprocket_awb: "AWB123458"
        }
      ];
      setOrders(sampleOrders);
    };

    loadOrders();
  }, [loadDeliveryStatuses]);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.paymentId && order.paymentId.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterStatus === "all" || order.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading orders...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content-header">
        <h1>Order Management</h1>
        <div className="admin-actions">
          <button className="export-btn" onClick={() => {
            const data = { orders: orders, exportDate: new Date().toISOString() };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `admin-orders-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}>
            📊 Export Orders
          </button>
          <button 
            className="export-btn" 
            onClick={() => loadDeliveryStatuses(orders)}
            style={{ backgroundColor: '#28a745' }}
          >
            🚚 Refresh Delivery Status
          </button>
          <button 
            className="export-btn" 
            onClick={() => {
              
              alert(`Orders count: ${orders.length}\nOrders with createdAt: ${orders.filter(o => o.createdAt).length}`);
            }}
            style={{ backgroundColor: '#ff6b6b' }}
          >
            🔍 Debug Created At
          </button>
        </div>
      </div>

      <div className="orders-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search orders by ID, user, or payment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-box">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Orders</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Orders</h3>
          <p className="stat-number">{orders.length}</p>
        </div>
        <div className="stat-card">
          <h3>Paid Orders</h3>
          <p className="stat-number">{orders.filter(order => order.status === 'paid').length}</p>
        </div>
        <div className="stat-card">
          <h3>Pending Orders</h3>
          <p className="stat-number">{orders.filter(order => order.status === 'pending').length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-number">₹{orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0).toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Delivered Orders</h3>
          <p className="stat-number">
            {Object.values(deliveryStatuses).filter(status => 
              status && status.status && status.status.toLowerCase().includes('delivered')
            ).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>In Transit</h3>
          <p className="stat-number">
            {Object.values(deliveryStatuses).filter(status => 
              status && status.status && (
                status.status.toLowerCase().includes('transit') || 
                status.status.toLowerCase().includes('shipped')
              )
            ).length}
          </p>
        </div>
      </div>

      <div className="orders-table">
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>User ID</th>
              <th>Status</th>
              <th>Total</th>
              <th>Payment ID</th>
              <th>Shiprocket Order</th>
              <th>AWB Number</th>
              <th>Delivery Status</th>
              <th style={{
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '150px',
                position: 'sticky',
                right: '0',
                zIndex: '10'
              }}>
                📅 Created At
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => {
              const deliveryStatus = deliveryStatuses[order.orderId];
              const isLoadingDelivery = loadingDelivery[order.orderId];
              
              return (
                <tr key={order.orderId}>
                  <td>
                    <div className="order-id">
                      <FaBox />
                      <span>{order.orderId}</span>
                    </div>
                  </td>
                  <td>{order.userId}</td>
                  <td>
                    <span className={`status-badge ${order.status || 'unknown'}`}>
                      {order.status || 'unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="order-total">
                      <FaRupeeSign />
                      <span>{order.total || '0'}</span>
                    </div>
                  </td>
                  <td>{order.paymentId || 'N/A'}</td>
                  <td>{order.shiprocket_order_id || 'N/A'}</td>
                  <td>{order.shiprocket_awb || 'N/A'}</td>
                  <td>
                    {isLoadingDelivery ? (
                      <div className="delivery-loading">🔄 Loading...</div>
                    ) : deliveryStatus ? (
                      <div className="delivery-status">
                        <div className="delivery-status-main">
                          {getDeliveryStatusIcon(deliveryStatus.status)}
                          <span 
                            className="delivery-status-text"
                            style={{ color: getDeliveryStatusColor(deliveryStatus.status) }}
                          >
                            {deliveryStatus.status}
                          </span>
                        </div>
                        {deliveryStatus.courier !== 'N/A' && (
                          <div className="delivery-courier">
                            <small>🚛 {deliveryStatus.courier}</small>
                          </div>
                        )}
                        {deliveryStatus.etd && (
                          <div className="delivery-etd">
                            <small>📅 ETA: {new Date(deliveryStatus.etd).toLocaleDateString()}</small>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="delivery-no-data">
                        <span>❌ No tracking data</span>
                      </div>
                    )}
                  </td>
                  <td style={{
                    backgroundColor: '#f0f8ff',
                    border: '2px solid #007bff',
                    fontWeight: 'bold',
                    color: '#007bff',
                    minWidth: '150px',
                    position: 'sticky',
                    right: '0',
                    zIndex: '10'
                  }}>
                    {order.createdAt ? (
                      <div className="created-at">
                        <div className="created-date">
                          📅 {new Date(order.createdAt).toLocaleDateString()}
                        </div>
                        <div className="created-time">
                          🕐 {new Date(order.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    ) : (
                      <span className="no-date" style={{ color: 'red', fontWeight: 'bold' }}>
                        ❌ NO DATE
                      </span>
                    )}
                  </td>
                  <td>
                    <button 
                      className="view-details-btn"
                      onClick={() => alert(`Order Details:\n${JSON.stringify(order, null, 2)}`)}
                    >
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredOrders.length === 0 && (
        <div className="no-orders">
          <FaBox />
          <p>No orders found matching your criteria.</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders; 